import React, { useCallback, useEffect, useRef, useState } from "react";
import { Slider } from "primereact/slider";
import { Dropdown } from "primereact/dropdown";
import { Midi } from "@tonejs/midi";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMusic, faDrum, faRecordVinyl, faBell, faWaveSquare, faGuitar } from '@fortawesome/free-solid-svg-icons';

interface CustomMidiPlayerProps {
  midiData: Uint8Array;
  showCanvasProgress?: boolean;
}

interface NoteEvent {
  time: number;
  note: string;
  duration: number;
  frequency: number;
}

interface ActiveOscillator {
  osc: OscillatorNode;
  note: string;
  frequency: number;
  endTime: number;
}


// Converti nome nota in frequenza
const noteToFrequency = (note: string): number => {
  const notes: { [key: string]: number } = {
    'C': 0, 'C#': 1, 'Db': 1, 'D': 2, 'D#': 3, 'Eb': 3,
    'E': 4, 'F': 5, 'F#': 6, 'Gb': 6, 'G': 7, 'G#': 8,
    'Ab': 8, 'A': 9, 'A#': 10, 'Bb': 10, 'B': 11
  };

  const match = note.match(/^([A-G][#b]?)(\d+)$/);
  if (!match) return 440;

  const noteName = match[1];
  const octave = parseInt(match[2]);
  const semitone = notes[noteName];

  // A4 = 440Hz, MIDI note 69
  const midiNote = semitone + (octave + 1) * 12;
  return 440 * Math.pow(2, (midiNote - 69) / 12);
};

type BuiltInInstrumentKey =
  | "piano"
  | "trumpet"
  | "flute"
  | "organ"
  | "strings"
  | "bell"
  | "pad";

type InstrumentKey = BuiltInInstrumentKey | "custom";

interface InstrumentPreset {
  label: string;
  type: OscillatorType;
  attack: number;
  decay: number;
  release: number;
  sustain: number;
}

const INSTRUMENT_PRESETS: Record<BuiltInInstrumentKey, InstrumentPreset> = {
  piano: {
    label: "Piano",
    type: "sine",
    attack: 0.01,
    decay: 0.08,
    release: 0.3,
    sustain: 0.9,
  },
  trumpet: {
    label: "Trumpet",
    type: "sawtooth",
    attack: 0.04,
    decay: 0.08,
    release: 0.45,
    sustain: 0.6,
  },
  flute: {
    label: "Flute",
    type: "sine",
    attack: 0.08,
    decay: 0.1,
    release: 0.6,
    sustain: 0.4,
  },
  organ: {
    label: "Organ",
    type: "square",
    attack: 0.02,
    decay: 0.05,
    release: 0.8,
    sustain: 0.7,
  },
  strings: {
    label: "Strings",
    type: "sawtooth",
    attack: 0.12,
    decay: 0.2,
    release: 0.9,
    sustain: 0.65,
  },
  bell: {
    label: "Bell",
    type: "sine",
    attack: 0.001,
    decay: 0.4,
    release: 1.2,
    sustain: 0.2,
  },
  pad: {
    label: "Synth Pad",
    type: "sawtooth",
    attack: 0.15,
    decay: 0.35,
    release: 1.4,
    sustain: 0.75,
  },
};

const instrumentIcons = {
  piano: faMusic,
  trumpet: faDrum,
  flute: faRecordVinyl,
  organ: faWaveSquare,
  strings: faGuitar,
  bell: faBell,
  pad: faWaveSquare,
  custom: faMusic,
};

const builtInInstrumentOptions = (Object.keys(INSTRUMENT_PRESETS) as BuiltInInstrumentKey[]).map(key => ({
  label: INSTRUMENT_PRESETS[key].label,
  value: key,
}));

const CUSTOM_INSTRUMENT_OPTION = { label: "Custom", value: "custom" as const };
const instrumentOptions = [...builtInInstrumentOptions, CUSTOM_INSTRUMENT_OPTION];

const itemTemplate = (option: { label: string; value: string }) => {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
      <FontAwesomeIcon icon={instrumentIcons[option.value as keyof typeof instrumentIcons]} style={{ width: '1rem' }} />
      <span>{option.label}</span>
    </div>
  );
};

const valueTemplate = (option: { label: string; value: string } | null) => {
  if (!option) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
      <FontAwesomeIcon icon={instrumentIcons[option.value as keyof typeof instrumentIcons]} style={{ width: '1rem' }} />
      <span>{option.label}</span>
    </div>
  );
};

const oscillatorTypeOptions: { label: string; value: OscillatorType }[] = [
  { label: "Sine", value: "sine" },
  { label: "Square", value: "square" },
  { label: "Sawtooth", value: "sawtooth" },
  { label: "Triangle", value: "triangle" },
];

const NOTE_TIME_TOLERANCE = 0.003;
type WaveformArray = Parameters<AnalyserNode['getByteTimeDomainData']>[0];

export const CustomMidiPlayer = ({ midiData, showCanvasProgress = false }: CustomMidiPlayerProps) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(70);
  const [isLoading, setIsLoading] = useState(true);
  const [activeNotes, setActiveNotes] = useState<string[]>([]);
  const [instrument, setInstrument] = useState<InstrumentKey>("piano");
  const currentInstrumentRef = useRef<InstrumentKey>("piano");

  const [customPreset, setCustomPreset] = useState<InstrumentPreset>({
    label: "Personalizzato",
    type: "sine",
    attack: 0.05,
    decay: 0.2,
    release: 0.6,
    sustain: 0.5,
  });

  const audioContextRef = useRef<AudioContext | null>(null);
  const gainNodeRef = useRef<GainNode | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const waveformDataRef = useRef<WaveformArray | null>(null);
  const notesRef = useRef<NoteEvent[]>([]);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const activeOscillatorsRef = useRef<Map<string, ActiveOscillator>>(new Map());
  const pausedAtRef = useRef(0);
  const hasStartedRef = useRef(false);

  const playbackRef = useRef({
    isPlaying: false,
    startAudioTime: 0,
    offsetTime: 0,
    intervalId: null as number | null,
    nextNoteIndex: 0,
  });

  useEffect(() => {
    try {
      const midi = new Midi(midiData);
      setDuration(midi.duration);

      const allNotes: NoteEvent[] = [];
      midi.tracks.forEach((track) => {
        track.notes.forEach((note) => {
          allNotes.push({
            time: note.time,
            note: note.name,
            duration: note.duration,
            frequency: noteToFrequency(note.name),
          });
        });
      });
      notesRef.current = allNotes.sort((a, b) => a.time - b.time);
      setIsLoading(false);
    } catch (error) {
      console.error("Error parsing MIDI:", error);
      setIsLoading(false);
    }

    return () => {
      stopPlayback();
      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }
    };
  }, [midiData]);

  useEffect(() => {
    if (gainNodeRef.current) {
      gainNodeRef.current.gain.value = volume / 100;
    }
  }, [volume]);

  const drawVisualization = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const width = canvas.width;
    const height = canvas.height;

    ctx.clearRect(0, 0, width, height);
    ctx.fillStyle = "rgba(50, 150, 150, 0.05)";
    ctx.fillRect(0, 0, width, height);

    const analyser = analyserRef.current;
    const waveformData = waveformDataRef.current;
    const progressHeight = showCanvasProgress ? 12 : 0;
    const progressGap = showCanvasProgress ? 4 : 0;
    const topPadding = 2;
    const bottomPadding = showCanvasProgress ? progressHeight + progressGap : 2;
    const drawableHeight = Math.max(4, height - topPadding - bottomPadding);
    const amplitude = drawableHeight / 2;
    const midLine = topPadding + amplitude;
    const shouldShowFlatWaveform = !hasStartedRef.current && !isPlaying && currentTime === 0;

    ctx.beginPath();
    ctx.lineWidth = 2;
    ctx.strokeStyle = "#FF960A";

    if (analyser && waveformData) {
      if (!shouldShowFlatWaveform) {
        analyser.getByteTimeDomainData(waveformData);
      } else {
        waveformData.fill(128);
      }

      const sliceWidth = width / waveformData.length;
      let x = 0;

      for (let i = 0; i < waveformData.length; i++) {
        const normalized = (waveformData[i] - 128) / 128;
        const y = midLine + normalized * amplitude;

        if (i === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }

        x += sliceWidth;
      }
    } else {
      ctx.moveTo(0, midLine);
      ctx.lineTo(width, midLine);
    }

    ctx.stroke();

    if (showCanvasProgress) {
      const progressX = duration > 0 ? (currentTime / duration) * width : 0;
      const progressY = height - progressHeight;
      ctx.fillStyle = "#FF960A";
      ctx.fillRect(0, progressY, progressX, progressHeight);
      ctx.fillStyle = "rgba(50, 150, 150, 0.2)";
      ctx.fillRect(progressX, progressY, width - progressX, progressHeight);
    }
  }, [showCanvasProgress, isPlaying, currentTime, duration]);

  useEffect(() => {
    drawVisualization();

    if (isPlaying) {
      const interval = setInterval(drawVisualization, 50);
      return () => clearInterval(interval);
    }
  }, [drawVisualization, activeNotes, currentTime, duration, isPlaying]);

  const initAudio = async (): Promise<AudioContext> => {
    if (!audioContextRef.current) {
      audioContextRef.current = new AudioContext();
    }

    const ctx = audioContextRef.current;

    if (ctx && !gainNodeRef.current) {
      gainNodeRef.current = ctx.createGain();
      gainNodeRef.current.gain.value = volume / 100;
    }

    if (ctx && gainNodeRef.current && !analyserRef.current) {
      analyserRef.current = ctx.createAnalyser();
      analyserRef.current.fftSize = 2048;
      waveformDataRef.current = new Uint8Array(analyserRef.current.frequencyBinCount) as WaveformArray;
      gainNodeRef.current.connect(analyserRef.current);
      analyserRef.current.connect(ctx.destination);
    }

    // Resume if suspended and wait for it
    if (ctx.state === 'suspended') {
      await ctx.resume();
    }

    return ctx;
  };

  const playNote = (frequency: number, noteDuration: number, noteName: string) => {
    if (!audioContextRef.current || !gainNodeRef.current) return;

    const currentInstrument = currentInstrumentRef.current;

    const ctx = audioContextRef.current;
    const now = ctx.currentTime;
    const clampedDuration = Math.max(0, noteDuration);
    if (clampedDuration === 0) return;

    const preset = currentInstrument === "custom" ? customPreset : INSTRUMENT_PRESETS[currentInstrument];

    const osc = ctx.createOscillator();
    const noteGain = ctx.createGain();

    osc.type = preset.type;
    osc.frequency.value = frequency;

    // Envelope ADSR basato sul preset
    const attackTime = preset.attack;
    const decayTime = preset.decay;
    const releaseTime = Math.min(preset.release, Math.max(0.05, clampedDuration * 0.6));
    const sustainLevel = preset.sustain;
    const attackEnd = now + attackTime;
    const decayEnd = attackEnd + decayTime;
    const sustainEnd = Math.max(decayEnd, now + Math.max(0, clampedDuration - releaseTime));

    noteGain.gain.cancelScheduledValues(now);
    noteGain.gain.setValueAtTime(0.0001, now);
    noteGain.gain.linearRampToValueAtTime(1, attackEnd);
    noteGain.gain.linearRampToValueAtTime(sustainLevel, decayEnd);
    noteGain.gain.setValueAtTime(sustainLevel, sustainEnd);
    noteGain.gain.linearRampToValueAtTime(0.0001, sustainEnd + releaseTime);

    osc.connect(noteGain);
    noteGain.connect(gainNodeRef.current);

    osc.start(now);
    osc.stop(sustainEnd + releaseTime + 0.05);

    const noteId = noteName + now;
    activeOscillatorsRef.current.set(noteId, {
      osc,
      note: noteName,
      frequency,
      endTime: now + clampedDuration,
    });

    osc.onended = () => {
      activeOscillatorsRef.current.delete(noteId);
    };

    setActiveNotes(prev => [...prev, noteName].slice(-20));

    setTimeout(() => {
      setActiveNotes(prev => {
        const idx = prev.lastIndexOf(noteName);
        if (idx > -1) {
          const newArr = [...prev];
          newArr.splice(idx, 1);
          return newArr;
        }
        return prev;
      });
    }, clampedDuration * 1000);
  };

  // Aggiorna currentInstrumentRef quando cambia instrument
  useEffect(() => {
    currentInstrumentRef.current = instrument;
  }, [instrument]);

  const startPlayback = async () => {
    const ctx = await initAudio();

    playbackRef.current.isPlaying = true;
    playbackRef.current.startAudioTime = ctx.currentTime;
    playbackRef.current.offsetTime = currentTime;
    playbackRef.current.nextNoteIndex = 0;

    // Trova il primo indice di nota da suonare
    const notes = notesRef.current;
    for (let i = 0; i < notes.length; i++) {
      if (notes[i].time >= currentTime - NOTE_TIME_TOLERANCE) {
        playbackRef.current.nextNoteIndex = i;
        break;
      }
    }

    // Schedula le note
    const scheduleNotes = () => {
      if (!playbackRef.current.isPlaying) return;

      const ctx = audioContextRef.current;
      if (!ctx) return;

      const currentAudioTime = ctx.currentTime;
      const elapsedTime = currentAudioTime - playbackRef.current.startAudioTime;
      const playbackTime = playbackRef.current.offsetTime + elapsedTime;

      // Schedula note che devono partire nei prossimi 100ms
      const lookahead = 0.1;
      const notes = notesRef.current;

      while (playbackRef.current.nextNoteIndex < notes.length) {
        const note = notes[playbackRef.current.nextNoteIndex];
        if (note.time > playbackTime + lookahead) break;

        const delay = Math.max(0, note.time - playbackTime);
        setTimeout(() => {
          if (playbackRef.current.isPlaying) {
            playNote(note.frequency, note.duration, note.note);
          }
        }, delay * 1000);

        playbackRef.current.nextNoteIndex++;
      }

      // Aggiorna il tempo corrente
      setCurrentTime(Math.min(playbackTime, duration));

      // Controlla se abbiamo finito
      if (playbackTime >= duration) {
        handleStop();
      }
    };

    // Avvia il loop di scheduling
    playbackRef.current.intervalId = window.setInterval(scheduleNotes, 50);
    scheduleNotes(); // Esegui immediatamente
  };

  const stopPlayback = () => {
    playbackRef.current.isPlaying = false;

    if (playbackRef.current.intervalId !== null) {
      clearInterval(playbackRef.current.intervalId);
      playbackRef.current.intervalId = null;
    }

    // Stoppa tutte le note attive
    activeOscillatorsRef.current.forEach(entry => {
      try {
        entry.osc.stop();
      } catch (e) {
        // Ignora errori se già stoppato
      }
    });
    activeOscillatorsRef.current.clear();
    setActiveNotes([]);
  };

  const handlePlay = async () => {
    if (!hasStartedRef.current) {
      hasStartedRef.current = true;
    }
    setIsPlaying(true);
    await startPlayback();
  };

  const handlePause = () => {
    setIsPlaying(false);
    pausedAtRef.current = currentTime;
    stopPlayback();
  };

  const handleStop = () => {
    setIsPlaying(false);
    stopPlayback();
    setCurrentTime(0);
    playbackRef.current.offsetTime = 0;
    playbackRef.current.nextNoteIndex = 0;
  };

  const handleSeek = (newTime: number) => {
    const wasPlaying = isPlaying;

    if (wasPlaying) {
      stopPlayback();
    }

    setCurrentTime(newTime);
    playbackRef.current.offsetTime = newTime;

    if (wasPlaying) {
      startPlayback();
    }
  };

  const formatTime = (time: number): string => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleInstrumentChange = (newInstrument: InstrumentKey) => {
    setInstrument(newInstrument);
    currentInstrumentRef.current = newInstrument;

    // Ferma gli oscillatori attivi per evitare glitch
    if (isPlaying) {
      activeOscillatorsRef.current.forEach((data) => {
        try {
          data.osc.stop();
        } catch (e) {
          // Ignore if already stopped
        }
      });
      activeOscillatorsRef.current.clear();
      setActiveNotes([]);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-content-center align-items-center p-4">
        <i className="pi pi-spin pi-spinner" style={{ fontSize: "2rem", color: "var(--button-bg)" }} />
      </div>
    );
  }

  return (
    <div className="custom-midi-player">
      <canvas
        ref={canvasRef}
        width={800}
        height={200}
        style={{
          width: "100%",
          height: "200px",
          borderRadius: "8px",
          marginBottom: "1rem",
        }}
      />

      <div className="player-controls" style={{ position: "relative", zIndex: 100 }}>
        <div className="player-buttons">
          {!isPlaying ? (
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); console.log("PLAY CLICK"); handlePlay(); }}
              className="player-button primary"
            >
              <i className="pi pi-play" />
            </button>
          ) : (
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); console.log("PAUSE CLICK"); handlePause(); }}
              className="player-button primary"
            >
              <i className="pi pi-pause" />
            </button>
          )}
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); console.log("STOP CLICK"); handleStop(); }}
            className="player-button secondary"
          >
            <i className="pi pi-stop" />
          </button>
        </div>
        <div className="timeline-container">
          <span className="time-display time-current">
            {formatTime(currentTime)}
          </span>
          <div className="timeline-slider">
            <Slider
              value={currentTime}
              onChange={(e) => handleSeek(e.value as number)}
              max={duration}
              step={0.1}
            />
          </div>
          <span className="time-display time-total">
            {formatTime(duration)}
          </span>
        </div>
      </div>

      <div className="player-settings">
        <div className="volume-control">
          <i className="pi pi-volume-down" />
          <Slider
            value={volume}
            onChange={(e) => setVolume(e.value as number)}
            max={100}
          />
          <i className="pi pi-volume-up" />
          <span>{volume}%</span>
        </div>
        <div className="instrument-and-mode-group">
          <div className="instrument-select">
            <Dropdown
              value={instrument}
              options={instrumentOptions}
              onChange={(e) => handleInstrumentChange(e.value as InstrumentKey)}
              itemTemplate={itemTemplate}
              valueTemplate={valueTemplate}
            />
          </div>
        </div>
      </div>

      {instrument === "custom" && (
        <div className="custom-envelope">
          <div className="flex align-items-center gap-2">
            <span style={{ fontFamily: "var(--font-family)", fontSize: "0.9rem" }}>Tipo onda</span>
            <Dropdown
              value={customPreset.type}
              options={oscillatorTypeOptions}
              onChange={(e) => setCustomPreset(prev => ({ ...prev, type: e.value, label: prev.label }))}
              style={{ minWidth: "180px" }}
            />
          </div>
          <div className="flex flex-column gap-2">
            <div className="flex align-items-center gap-3">
              <span style={{ minWidth: "90px", fontSize: "0.85rem" }}>Attack</span>
              <Slider
                value={customPreset.attack}
                min={0.005}
                max={0.5}
                step={0.005}
                onChange={(e) => setCustomPreset(prev => ({ ...prev, attack: e.value as number, label: prev.label }))}
                style={{ flex: 1 }}
              />
              <span style={{ width: "50px", textAlign: "right", fontSize: "0.85rem" }}>
                {customPreset.attack.toFixed(2)}s
              </span>
            </div>
            <div className="flex align-items-center gap-3">
              <span style={{ minWidth: "90px", fontSize: "0.85rem" }}>Decay</span>
              <Slider
                value={customPreset.decay}
                min={0.01}
                max={1}
                step={0.01}
                onChange={(e) => setCustomPreset(prev => ({ ...prev, decay: e.value as number, label: prev.label }))}
                style={{ flex: 1 }}
              />
              <span style={{ width: "50px", textAlign: "right", fontSize: "0.85rem" }}>
                {customPreset.decay.toFixed(2)}s
              </span>
            </div>
            <div className="flex align-items-center gap-3">
              <span style={{ minWidth: "90px", fontSize: "0.85rem" }}>Release</span>
              <Slider
                value={customPreset.release}
                min={0.1}
                max={1.5}
                step={0.01}
                onChange={(e) => setCustomPreset(prev => ({ ...prev, release: e.value as number, label: prev.label }))}
                style={{ flex: 1 }}
              />
              <span style={{ width: "50px", textAlign: "right", fontSize: "0.85rem" }}>
                {customPreset.release.toFixed(2)}s
              </span>
            </div>
            <div className="flex align-items-center gap-3">
              <span style={{ minWidth: "90px", fontSize: "0.85rem" }}>Sustain</span>
              <Slider
                value={customPreset.sustain}
                min={0.2}
                max={0.9}
                step={0.01}
                onChange={(e) => setCustomPreset(prev => ({ ...prev, sustain: e.value as number, label: prev.label }))}
                style={{ flex: 1 }}
              />
              <span style={{ width: "50px", textAlign: "right", fontSize: "0.85rem" }}>
                {customPreset.sustain.toFixed(2)}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
